// i18n — Spanish strings (parallel to en.ts).
//
// Every key in en.ts MUST exist here with the same structure. The
// TypeScript type enforces this at compile time (see index.ts).

import type { Translations } from './en';

const es: Translations = {
  // ── Producto ───────────────────────────────────────────────────────
  product: {
    name: 'As Oasis',
    tagline: 'IDE de IA para founders',
  },

  // ── Verbos de estado (Layer 7 D70) ─────────────────────────────────
  status: {
    running: 'Trabajando',
    waiting: 'Esperando',
    finished: 'Listo',
    error: 'Falló',
    interrupted: 'Parado',
    stuck: 'Bloqueado',
    archived: 'Archivado',
    // aria-label atom-level para `StatusIcon`. Tras sprint
    // `sidebar-status-icons-binary` (2026-05-12) es la única copy de
    // "inactivo" que sobrevive — el badge a nivel sidebar
    // (`agentList.idle` = 'Inactivo · Envía un mensaje') se eliminó
    // cuando `CircleCheck` asumió la señal de "listo para input".
    idle: 'Inactivo',
    needsYou: 'Te necesita',
    unconfirmed: 'Sin confirmar',
  },

  // ── Topbar ─────────────────────────────────────────────────────────
  topbar: {
    openMenu: 'Abrir menú',
    closeMenu: 'Cerrar menú',
    signOut: 'Cerrar sesión',
    notifications: 'Notificaciones',
    noNotifications: 'Sin notificaciones',
    unreadAgents: '{count} agentes sin leer',
    settings: 'Ajustes',
    accountMenu: 'Menú de cuenta',
    theme: 'Tema',
  },

  // ── Estado de conexión ─────────────────────────────────────────────
  connection: {
    connected: 'Conectado',
    reconnecting: 'Reconectando',
    disconnected: 'Desconectado — toca para reintentar',
  },

  // Sprint runner-picker-on-create — modal al pulsar "+" para que el
  // founder elija a qué runner va el nuevo agente, viendo capacidad
  // restante en el momento de elegir.
  runnerPicker: {
    title: 'Elige un runner',
    subtitle: 'El nuevo agente se crea en el runner que elijas.',
    autoLabel: 'Auto (recomendado)',
    autoHelper: 'Deja que As Oasis use el runner con más capacidad libre.',
    capacityFree: '{percent}% libre',
    capacityUnknown: 'Sin datos de capacidad',
    // Sprint runner-picker-session-primary — línea secundaria bajo el
    // "% libre" grande de session 5h. Se compone uniendo los buckets
    // con " · ". Misma unidad ("libre") que la primaria.
    bucketWeekly: 'Weekly {percent}% libre',
    bucketSonnet: 'Sonnet {percent}% libre',
    // Sprint usage-limits-v2 (2026-07-08) — límites scoped por modelo
    // desde el array canónico `limits[]`.
    bucketWeeklyScoped: 'Weekly · scoped {percent}% libre',
    bucketWeeklyScopedModel: 'Weekly · {model} {percent}% libre',
    offlineReason: 'Offline — elige otro runner.',
    drainingReason: 'En drenado — no acepta nuevos agentes.',
    // Sprint runner-quarantine-truth (D5, D013-EC-127) — los runners en
    // cuarentena siguen siendo seleccionables: fijar es el override
    // documentado del founder, y la leyenda lo convierte en una
    // decisión informada en vez de un bypass silencioso.
    quarantinedCaption: 'En cuarentena — puedes fijarlo igualmente.',
    confirm: 'Crear agente',
    cancel: 'Cancelar',
  },

  // ── Sidebar ────────────────────────────────────────────────────────
  sidebar: {
    newAgent: 'Nuevo Agente',
    newAgentDisabled: 'Agentes nuevos pausados — servidor bajo en memoria',
    search: 'Buscar agentes',
    resizeAriaLabel: 'Redimensionar barra lateral',
    empty: {
      title: 'Aún no hay agentes',
      subtitle: 'Crea el primero',
    },
    showMore: 'Mostrar {count} más',
    // Sprint agent-stages — las secciones por recencia (FIJADOS/HOY/AYER/
    // ANTERIORES) se retiraron; las secciones ahora son los estados de
    // workflow (grupo `stages` más abajo). ARCHIVADOS se mantiene.
    section: {
      archived: 'ARCHIVADOS',
    },
    project: {
      label: 'Proyecto',
      all: 'Todos los proyectos',
    },
  },

  // ── Estados de workflow (sprint agent-stages) ───────────────────────
  stages: {
    noStage: 'Sin estado',
    moveTo: 'Mover a estado',
    newStage: 'Nuevo estado…',
    manage: 'Gestionar estados…',
    manageTitle: 'Estados',
    createTitle: 'Nuevo estado',
    editTitle: 'Editar estado',
    newStageButton: 'Nuevo estado',
    emptyList: 'Aún no hay estados. Crea el primero.',
    nameLabel: 'Nombre',
    namePlaceholder: 'p. ej. Soak',
    colorLabel: 'Color',
    colorOption: 'Color {n}',
    create: 'Crear',
    save: 'Guardar',
    saving: 'Guardando…',
    cancel: 'Cancelar',
    rename: 'Editar',
    delete: 'Eliminar',
    moveUp: 'Subir',
    moveDown: 'Bajar',
    filterAll: 'Todos',
    deleteConfirmTitle: '¿Eliminar estado?',
    deleteConfirmBody:
      'Los {count} agentes de "{name}" pasarán a "Sin estado". Ningún agente se elimina.',
    createError: 'No se pudo crear el estado',
    createNeedsWorkspace: 'Elige primero un workspace — los estados pertenecen a un proyecto',
    updateError: 'No se pudo actualizar el estado',
    deleteError: 'No se pudo eliminar el estado',
  },

  // ── Columna de workspaces ───────────────────────────────────────────
  workspace: {
    addProject: 'Nuevo proyecto',
    newProject: 'Nuevo proyecto',
    repoUrl: 'URL del repo',
    slugInvalid: 'El slug debe empezar con letra o número y contener solo minúsculas, números, puntos, guiones.',
    cloning: 'Clonando…',
    creating: 'Creando…',
    create: 'Crear',
    done: 'Listo',
    namePlaceholder: 'Mi Proyecto',
    slugPlaceholder: 'mi-proyecto',
    repoPlaceholder: 'git@github.com:org/repo.git',
    branchPlaceholder: 'main',
  },

  // ── Personalización de workspace (Sprint 11b.2) ─────────────────────
  workspaceCustomization: {
    edit: 'Editar proyecto…',
    archive: 'Archivar',
    editTitle: 'Editar proyecto',
    // Sprint add-project-autoprovision — primitiva de reparación para
    // URLs de repo equivocadas (guardar re-ejecuta el setup; re-guardar
    // sin cambios es el gesto explícito de reintento).
    repoUrl: 'URL del repositorio',
    repoUrlHint: 'Al guardar se vuelve a ejecutar la configuración del proyecto en los runners.',
    repoUrlPlaceholder: 'https://github.com/owner/repo',
    repoUrlError: 'No se pudo actualizar la URL del repositorio. Inténtalo de nuevo.',
    initials: 'Iniciales',
    initialsHint: 'Exactamente 2 letras o números, en mayúsculas.',
    color: 'Color',
    autoColor: 'Color automático',
    autoGlyph: 'A',
    colorOption: 'Color {n}',
    resetAuto: 'Reiniciar a automático',
    save: 'Guardar',
    saving: 'Guardando…',
    reorderError: 'No pude reordenar — intenta de nuevo.',
    updateError: 'No pude actualizar — intenta de nuevo.',
    syncIncomplete: 'Sincronización incompleta en {count} servidor(es).',
  },

  // ── Menú contextual de agente ──────────────────────────────────────
  contextMenu: {
    markRead: 'Marcar leído',
    markUnread: 'Marcar no leído',
    rename: 'Renombrar',
    fork: 'Bifurcar',
    archive: 'Archivar',
    restore: 'Restaurar',
    delete: 'Eliminar',
    ariaLabel: 'Acciones del agente',
  },

  // Sprint move-runner-button (EC-135) — mover proactivo desde el menú ⋯.
  moveRunner: {
    menuItem: 'Mover a otro servidor',
    pickerTitle: 'Mover a otro servidor',
    pickerConfirm: 'Mover aquí',
    confirmTitle: '¿Mover la conversación de servidor?',
    confirmBody:
      'La memoria de la conversación viaja completa. Si hay cambios sin guardar, se empaquetan en una rama wip-move, se suben a GitHub y el agente los recupera en el servidor nuevo.',
    confirmConfirm: 'Mover',
    successToast: 'Conversación movida a {hostname}.',
    errorToast: 'No se pudo mover: {reason}',
  },

  // ── Header del chat ────────────────────────────────────────────────
  chatHeader: {
    renameTooltipDesktop: 'Doble clic para renombrar',
    renameTooltipMobile: 'Toca para renombrar',
    moreActions: 'Más acciones',
    backAriaLabel: 'Volver a la lista de agentes',
    // Sprint fc-conversation-search (§2.2) — disparador de la lupa,
    // inmediatamente a la izquierda del menú ⋯. La razón de deshabilitado
    // viene de conversationSearch.loading / .loadError (Gate 2 ítem 9).
    search: 'Buscar en esta conversación',
  },

  // ── Chat — estado vacío ────────────────────────────────────────────
  chat: {
    empty: {
      title: 'Elige un agente o crea uno nuevo',
      subtitle: 'Selecciona un agente del sidebar para ver sus mensajes, enviar un seguimiento o detener la ejecución.',
      openSidebar: 'Mostrar lista de agentes',
    },
    // Sprint S20.5 PR-4 (D013-EC-70) — pill del chat-header para el
    // estado `waiting`. Espejo del badge del sidebar pero con una pista
    // más larga porque el chat header tiene más espacio horizontal y es
    // la superficie principal del agente seleccionado. Reviewer fix
    // (PR #149): el copy en castellano evita el préstamo "Idle" y usa
    // "Inactivo" para mantener `founder-first-vision.md` §5 (plain
    // Spanish — el founder lee la dashboard en es).
    idleHint: 'Inactivo · Envía un mensaje para continuar.',
  },

  // ── Composer ────────────────────────────────────────────────────────
  composer: {
    heading: 'Nuevo agente',
    openSidebar: 'Abrir sidebar',
    placeholder: {
      new: '¿Qué tiene que hacer este agente?',
      continue: 'Envía un mensaje para continuar...',
      waiting: 'Envía un mensaje · / para comandos · @ para menciones',
      running: 'Añade un seguimiento…',
      stuck: 'Envía un mensaje — el agente lo recibirá.',
      finished: 'Envía un mensaje para continuar este agente…',
      interrupted: 'Envía un mensaje para continuar este agente…',
      errorRecoverable: 'Envía un mensaje para continuar (se reintentará)…',
      idle: 'Envía un mensaje…',
      working: 'Añade un seguimiento…',
      disconnected: 'Se enviará al reconectar',
      archived: 'Este agente está archivado. Restáuralo para continuar.',
      permissionPending: 'Esperando tu decisión de permiso arriba',
      default: 'Envía un mensaje…',
      exampleHint: 'Lee el README y resúmelo en 5 puntos...  ·  escribe / para comandos, @ para menciones',
    },
    hint: {
      running: 'Esperando a que el agente termine el turno actual…',
      stuck: 'Sin actividad durante varios minutos — el proceso sigue activo pero puede estar atascado.',
      finished: 'Agente listo — envía para continuar o bifurca para ramificar.',
      interrupted: 'Agente detenido — envía para reanudar o bifurca para ramificar.',
      error: 'Agente falló — envía para reintentar, o bifurca desde el último turno funcional.',
    },
    send: 'Enviar',
    sending: 'Enviando…',
    starting: 'Iniciando…',
    stop: 'Parar',
    stopping: 'Parando…',
    stopAriaLabel: 'Parar agente',
    sendAriaLabel: 'Enviar mensaje',
    attachAriaLabel: 'Adjuntar archivo',
    promptLabel: 'Prompt',
    promptTooLong: 'Prompt demasiado largo — elimina {count} caracteres para enviar.',
    promptApproachingLimit: 'Acercándose al límite del prompt.',
    enterToSend: 'Enter para enviar · Shift + Enter para nueva línea',
    memoryWarning: 'Memoria del servidor baja — agentes nuevos pausados hasta que se libere RAM.',
    couldNotStart: 'No se pudo iniciar el agente.',
    couldNotChangeAgent: 'No se pudo cambiar el agente',
    sendFailed: 'Mensaje no enviado. Toca para reintentar.',
    attachmentLost: 'Una imagen se perdió. Vuelve a adjuntarla y envía de nuevo.',
    attachmentsLost: 'Algún adjunto se perdió. Vuelve a adjuntarlos y envía de nuevo.',
    // Sprint attachments-anthropic-parity Phase D — etiquetas accesibles
    // para la miniatura clickable + lightbox de imagen (D7, D8, D9).
    imageLightboxOpen: 'Ver imagen completa',
    imageLightboxClose: 'Cerrar imagen',
    removeAttachmentAriaLabel: 'Quitar adjunto',
    retryAttachmentAriaLabel: 'Reintentar adjunto',
    // Sprint work-segment-collapse (D013-EC-526, Gate 2 minor issue 4).
    imageUnavailable: 'No se pudo cargar la imagen',
    confirmModeSwitch: {
      toPlan: '¿Cambiar a modo Plan?',
      toAgent: '¿Cambiar a modo Agente?',
      body: 'Una acción está esperando tu aprobación. Cambiar de modo la cancelará.',
      confirmPlan: 'Cambiar a Plan',
      confirmAgent: 'Cambiar a Agente',
    },
  },

  // ── Diálogos de confirmación ───────────────────────────────────────
  confirm: {
    cancel: 'Cancelar',
    confirm: 'Confirmar',
    working: 'Procesando…',
    stopAgent: {
      title: '¿Parar este agente?',
      body: 'El proceso de Claude actual se terminará. El historial del chat queda en el archivo.',
      confirm: 'Parar',
    },
    forkAgent: {
      title: '¿Bifurcar este agente?',
      body: 'Un nuevo agente retoma la misma sesión de Claude. Ambos comparten historial hasta este punto y divergen después.',
      confirm: 'Bifurcar',
    },
    deleteAgent: {
      title: '¿Eliminar este agente?',
      body: 'El proceso se terminará y el agente desaparecerá de la lista activa. No se puede deshacer.',
      confirm: 'Eliminar',
    },
    deleteAgentSidebar: {
      title: '¿Eliminar agente?',
      bodyTemplate: '"{name}" se detendrá y eliminará. Tienes 5 segundos para deshacer.',
      confirm: 'Eliminar',
    },
    signOut: {
      title: '¿Cerrar sesión?',
      body: 'Necesitarás iniciar sesión de nuevo para acceder al dashboard.',
      confirm: 'Cerrar sesión',
    },
    resetSettings: {
      title: '¿Restablecer todos los ajustes?',
      body: 'Esto restaura los valores predeterminados. No se puede deshacer.',
      confirm: 'Restablecer',
    },
    changeMode: {
      title: '¿Cambiar a modo {mode}?',
      body: 'El agente se reiniciará bajo el nuevo modo. El historial del chat se conserva; el proceso de Claude que está corriendo se reinicia.',
      confirm: 'Cambiar',
    },
  },

  // ── Toasts ─────────────────────────────────────────────────────────
  toast: {
    undo: 'Deshacer',
    dismiss: 'Descartar notificación',
    agentArchived: 'Agente archivado',
    agentRestored: 'Agente restaurado',
    agentDeleted: 'Agente eliminado',
    agentStopped: 'Agente parado',
    undoFailed: 'No se pudo deshacer — el agente se eliminará en unos segundos',
    ariaLabel: 'Notificaciones',
    couldNotPin: 'No se pudo fijar el agente. Inténtalo de nuevo.',
    couldNotArchive: 'No se pudo archivar el agente. Inténtalo de nuevo.',
    couldNotRestore: 'No se pudo restaurar el agente. Inténtalo de nuevo.',
    couldNotDelete: 'No se pudo eliminar el agente. Inténtalo de nuevo.',
    couldNotRename: 'No se pudo renombrar el agente. Inténtalo de nuevo.',
    couldNotSave: 'No se pudo guardar. Inténtalo de nuevo.',
    retry: 'Reintentar',
    // Sprint 20.3 PR-10 (D013-EC-43) — etiqueta de la acción del toast
    // de spawn-error que abre el modal centralizado. El copy del toast
    // se movió a `errors.spawn.NO_RUNNER_AVAILABLE` en S20.5 PR-3
    // para que todas las ramas de spawn vivan en el mismo namespace.
    viewRunnerUsage: 'Ver uso de runners',
  },

  // ── Página de login ────────────────────────────────────────────────
  login: {
    passwordLabel: 'Contraseña',
    passwordEmpty: 'Introduce tu contraseña.',
    signIn: 'Iniciar sesión',
    signingIn: 'Iniciando sesión…',
    couldNotSignIn: 'No se pudo iniciar sesión. Inténtalo de nuevo.',
    wrongPassword: 'Contraseña incorrecta',
    tooManyAttempts: 'Demasiados intentos — espera {seconds}s',
  },

  // ── Pantalla de re-entrada de la puerta de acceso (sprint
  // access-session-recovery, D5) ────────────────────────────────────
  // Namespace propio, no `login.*`: es una capa DISTINTA (la puerta de
  // identidad delante de la app, D6) de la del formulario de contraseña, y
  // un significado distinto al de `SessionExpiredBanner` (esa es una
  // sesión del runner de Claude, no la puerta de seguridad). La copia evita
  // "Cloudflare", "JWT", "cookie", "service worker", "302" según D5 —
  // nombra la CAUSA ("tu sesión de seguridad"), no el mecanismo.
  accessGate: {
    title: 'Tu sesión de seguridad ha caducado',
    message: 'Vuelve a entrar para seguir usando As Oasis.',
    reenterButton: 'Volver a entrar',
    // Cambio 5 del retador (R1) — auto-reporte de EC-3: `?reauth=` está
    // presente en la URL Y seguimos en `gate-expired` tras el arranque, es
    // decir el viaje de ida y vuelta por el login no dejó sesión detrás.
    stillBlockedMessage: 'Eso no terminó de funcionar. Vuelve a intentarlo.',
    // Cambio 12 del retador (R1) — defensa de motor. Si el navegador nunca
    // produce un `opaqueredirect` (observado en WebKit), un rechazo real
    // de la puerta se clasifica como `NETWORK_ERROR` y el formulario de
    // contraseña se queda. Esta afordancia secundaria, no automática, hace
    // la misma navegación de D3 sin depender de que el clasificador diga
    // "puerta".
    networkErrorHint: '¿No puedes entrar? Puede que la contraseña no sea el problema.',
    networkErrorButton: 'Volver a entrar',
    // DocViewerSheet — misma puerta, encontrada con un visor de doc abierto.
    docViewerBlocked: 'Tu sesión de seguridad ha caducado. Recarga la página para continuar.',
  },

  // ── Feed de notificaciones (Sprint notification-center) ────────────
  notifications: {
    title: 'Notificaciones',
    markAllRead: 'Marcar leídas',
    openAction: 'Ver',
  },

  // ── Página de ajustes ──────────────────────────────────────────────
  settings: {
    heading: 'Ajustes',
    backToChat: '← Volver al chat',
    display: {
      title: 'Visualización',
      description: 'Lo que ves en el chat por defecto.',
      developerMode: 'Mostrar detalles técnicos',
      developerModeDescription: 'Muestra llamadas de herramientas, diffs, comandos bash y entradas JSON en línea. Cuando está desactivado, cada llamada se resume en una línea; puedes expandir cada una con el ▽.',
    },
    conversations: {
      title: 'Conversaciones',
      description: 'Qué hace el sistema cuando un servidor se está quedando sin sitio.',
      autoFailover: 'Mover conversaciones automáticamente',
      autoFailoverDescription: 'Si el servidor de una conversación se está quedando sin sitio, el sistema la traslada sola a otro servidor y sigue trabajando. Desactívalo si prefieres moverla tú mismo con el botón de la conversación.',
    },
    notifications: {
      title: 'Notificaciones',
      description: 'Cómo te enteras de los agentes que no estás viendo.',
      tierA: 'Necesita tu acción',
      tierADescription: 'Aprobaciones y preguntas. Siempre muestra un aviso con sonido en la app; este interruptor además envía push a tus dispositivos.',
      tierB: 'Informativo',
      tierBDescription: 'Un agente termina, falla o se queda bloqueado. Aparece en la campana con un punto — sin push, sin sonido.',
      tierC: 'Problemas del sistema',
      tierCDescription: 'Algo está roto (disco, un servidor caído). Siempre te llega por Telegram y push — esto no se puede apagar.',
      sound: 'Sonido de notificación',
      soundDescription: 'Reproduce un tono corto cuando un agente necesita tu acción — incluso mientras estás viendo el dashboard.',
      pushDevice: 'Push en este dispositivo',
      pushDeviceDescription: 'Recibe notificaciones en este dispositivo aunque la app esté cerrada.',
      pushEnable: 'Activar',
      pushDisable: 'Desactivar',
      pushUnsupported: 'Este navegador no puede recibir push. En iPhone, instala primero la app: Compartir → Añadir a pantalla de inicio.',
      pushDenied: 'Las notificaciones están bloqueadas para esta app. Permítelas en los ajustes de tu dispositivo y vuelve.',
      pushFailServerDisabled: 'Las notificaciones aún no están activadas en el servidor. Se activarán con el próximo despliegue — inténtalo después.',
      pushFailPermissionDenied: 'No permitiste las notificaciones. Pulsa Activar de nuevo y elige Permitir.',
      pushFailNoRegistration: 'Esta app no está instalada del todo en este dispositivo. En iPhone: Compartir → Añadir a pantalla de inicio, y ábrela desde ahí.',
      pushFailError: 'No se pudo activar el push en este dispositivo. Inténtalo de nuevo en un momento.',
      testSend: 'Enviar notificación de prueba',
      testSending: 'Enviando…',
      testDisabledHint: 'Activa primero el push en este dispositivo y luego envía una prueba.',
      testSent: 'Enviada — mira tu dispositivo.',
      testRateLimited: 'Espera unos segundos antes de enviar otra.',
      testFailed: 'No se pudo enviar la prueba. Inténtalo de nuevo.',
      telegram: 'Enviar notificaciones a Telegram',
      telegramDescription: 'Duplica los avisos de agentes en tu grupo de Telegram. Los problemas del sistema siempre van a Telegram, independientemente de este interruptor.',
    },
    permissions: {
      title: 'Modo de permisos por defecto',
      description: 'Cómo los nuevos agentes manejan la ejecución de herramientas por defecto. Puedes cambiar por agente con el chip de modo.',
    },
    allowlist: {
      title: 'Permitidos siempre',
      description: 'Cosas que le has dicho a Claude que puede hacer sin preguntar. Olvida cualquiera y el agente preguntará la próxima vez.',
      loading: 'Cargando…',
      couldNotLoad: 'No se pudieron cargar los permisos guardados',
      empty: 'Aún no has permitido nada para siempre. Haz clic en "Permitir siempre para…" en una tarjeta de permiso para guardar una regla aquí.',
      forget: 'Olvidar',
      forgetting: 'Olvidando…',
      couldNotForget: 'No se pudo olvidar esta regla',
      showAll: 'Mostrar todos ({count})',
      toolKind: {
        Bash: 'Ejecutar comando',
        Read: 'Leer archivo',
        Edit: 'Editar archivo',
        Write: 'Escribir archivo',
        MultiEdit: 'Editar archivo',
        NotebookEdit: 'Editar notebook',
        Glob: 'Buscar archivos',
        Grep: 'Buscar en código',
        WebFetch: 'Obtener URL',
      },
    },
    language: {
      title: 'Idioma',
      description: 'Elige el idioma de la interfaz del dashboard.',
      english: 'Inglés',
      spanish: 'Español',
    },
    account: {
      title: 'Cuenta',
      description: 'Ajustes de tu cuenta.',
    },
    servers: {
      title: 'Servidores',
      description: 'Instancias VPS ejecutando agentes de As Oasis.',
      addServer: 'Añadir servidor',
      name: 'Nombre',
      baseUrl: 'URL base',
      id: 'ID del servidor',
      online: 'En línea',
      offline: 'Sin conexión',
      remove: 'Eliminar',
      save: 'Guardar',
      cancel: 'Cancelar',
      empty: 'No hay servidores configurados aún. Añade un servidor para empezar a gestionar agentes en múltiples VPS.',
      namePlaceholder: 'VPS 1',
      baseUrlPlaceholder: 'https://vps1.asoasis.com',
      idPlaceholder: 'vps1',
    },
    projects: {
      title: 'Proyectos',
      description: 'Repositorios de código gestionados por agentes.',
      addProject: 'Añadir proyecto',
      name: 'Nombre',
      slug: 'Slug',
      workdir: 'Directorio de trabajo',
      defaultBranch: 'Rama por defecto',
      archive: 'Archivar',
      unarchive: 'Restaurar',
      empty: 'No hay proyectos aún. Los proyectos se crean automáticamente cuando los agentes se inician.',
      save: 'Guardar',
      cancel: 'Cancelar',
    },
    // Sprint 20.3 PR-8 — panel admin de runners.
    runners: {
      title: 'Runners',
      description: 'Servidores que ejecutan el CLI de Claude para los agentes.',
      empty: 'No hay runners registrados todavía.',
      loadError: 'No se pudieron cargar los runners.',
      refresh: 'Actualizar',
      reload: 'Recargar runners',
      colHostname: 'Hostname',
      colStatus: 'Estado',
      colAccount: 'Cuenta',
      colCap: 'Usado · 5h',
      colWeeklySonnet: 'Sonnet · esta semana',
      colWeeklyOpus: 'Opus · esta semana',
      colActive: 'Activos',
      // Sprint fleet-resource-metrics (spec §2 D10).
      colCpu: 'CPU',
      colRam: 'RAM',
      colDisk: 'Disco',
      colTrend: 'Tendencia (6h)',
      // Sprint fleet-resource-metrics (spec §2 D10) — estado vacío del
      // sparkline + etiqueta accesible (i18n-enforce: sin literales JSX).
      trendEmpty: '—',
      trendAriaLabel: 'Tendencia de uso de CPU, RAM y disco',
      colLastSeen: 'Visto por última vez',
      colVersion: 'Versión',
      colActions: 'Acciones',
      // Sprint runner-checkout-freshness (ADR D-1073, mitad VISIBILIDAD)
      colCheckout: 'Checkout',
      checkoutSummary: '{commits} por detrás · {repo}',
      checkoutStale: '(desactualizado)',
      statusOnline: 'Online',
      statusOffline: 'Offline',
      statusDraining: 'Drenando',
      statusIncompatible: 'Versión incompatible',
      drain: 'Drenar',
      undrain: 'Reactivar',
      reEnable: 'Re-habilitar',
      confirmDrainTitle: '¿Drenar {hostname}?',
      confirmDrainBody: 'Drenar evita que se asignen nuevos agentes a {hostname} y espera a que los agentes en curso terminen antes de que el runner salga limpiamente.',
      confirmDrain: 'Drenar runner',
      unavailableReasonBlocked: 'Cuenta de Claude bloqueada. Resuélvelo con Anthropic y luego re-habilita.',
      unavailableReasonDiskFull: 'Disco lleno. Libera espacio en el runner y luego re-habilita.',
      unavailableReasonNotAuthed: 'Claude no autenticado. Ejecuta `claude login` en el runner y luego re-habilita.',
      unavailableReasonIncompatible: 'El runner está en una versión de protocolo incompatible. Despliega el binario correcto y luego re-habilita.',
      // Sprint runner-resilience-after-missing-credential §2.4.
      unavailableReasonMissingCredential: 'Una credencial de 1Password falló el healthcheck de arranque del runner. Revisa el log del runner para ver qué item falla y arréglalo en OFM. El runner se auto-recupera en ~10 min cuando se corrija.',
      // Sprint runner-op-token-single-source (defect 3) — workspace drift.
      unavailableReasonWrongWorkspace: 'El token de 1Password del runner autentica contra el workspace incorrecto. Esperado: santi-team.1password.com. El operador debe rotar /etc/asoasis/op-token vía el mecanismo OFM-resync (ver runbook).',
      drainSuccess: '{hostname} está drenando.',
      drainError: 'No se pudo drenar {hostname}: {reason}',
      undrainSuccess: '{hostname} vuelve a la rotación.',
      undrainError: 'No se pudo reactivar {hostname}: {reason}',
      clearUnavailableSuccess: '{hostname} re-habilitado.',
      clearUnavailableError: 'No se pudo re-habilitar {hostname}: {reason}',
      capUnknown: '—',
      lastSeenNever: 'nunca',
      hours: '{value}h',
      hoursOfLimit: '{value}h / {limit}h',
      activeAgents: '{count}',
    },
  },

  // ── System pills ───────────────────────────────────────────────────
  system: {
    agentStarted: 'Agente iniciado',
    newTurn: 'Nuevo turno',
    turnComplete: 'Turno completado',
    turnCompleteWithDuration: 'Turno completado · {duration}',
    agentExited: 'Agente {status}',
    switchedTo: 'Cambiado a {model} · {effort}',
    configChanged: 'Configuración cambiada',
    stopped: 'Parado',
    agentCrashed: 'Agente falló',
    reopenedConversation: 'Conversación reabierta',
    readyContinuing: 'Listo — continuando',
    // Sprint context-limit-survivable (Seam C) — SOLO FALLBACKS. El backend
    // escribe su propia frase en todas estas filas y la píldora la renderiza
    // tal cual; estas se usan solo si la fila no trae ninguna. Lenguaje claro,
    // con la siguiente acción cuando la hay, sin jerga (founder-first §5).
    conversationCompacted: 'Se ha acortado la conversación para poder seguir',
    messageNotDelivered: 'Ese mensaje no llegó — vuelve a escribirlo',
    // Sprint lazy-spawn-recovery (§2 D11) — SOLO FALLBACKS, mismo patrón
    // que las dos anteriores. El backend escribe la frase real en cada
    // fila (`renderFailoverFounderCopy` / el mensaje de la píldora de
    // recuperación).
    reassigned: 'Movido a otro servidor',
    recoveredNeverStarted: 'Conversación recuperada',
    // Founder-first §5 — es el ÚNICO texto que ve el fundador cuando una fila
    // `error` no trae ni `code` conocido ni `message`, que es justo el caso
    // para el que existe la regla de "nombra siempre la acción siguiente". La
    // única acción disponible sin saber qué falló: abrir una nueva.
    error:
      'Algo ha fallado y esta conversación no puede continuar — abre una conversación nueva para seguir',
    // Sprint context-limit-survivable (PR 1, pasada de copy de M9) — NO son
    // fallbacks. Son el texto que la píldora renderiza para los tres códigos
    // de `agent.error`, resuelto desde el `code` de la fila y no desde su
    // `message` guardado, de forma que las filas escritas antes de esta
    // pasada (inglés del backend con "Runner" / "session") quedan
    // corregidas al recargar en lugar de salir en rojo e ilegibles. Gemelas
    // de `FOUNDER_ERROR_COPY` en `backend/src/claude/session-manager.ts` —
    // mantener sincronizadas. Una acción siguiente en cada una; los tres
    // significados son distintos de verdad, así que el texto también.
    errorSessionLost:
      'Esta conversación se cortó. Escribe un mensaje aquí para retomarla.',
    errorSessionExpired:
      'Esta conversación caducó y ya no puede continuar. Lo que hay escrito se queda aquí; abre una conversación nueva para seguir.',
    errorRestartLoop:
      'Esta conversación se reiniciaba en bucle y la hemos detenido. Abre una conversación nueva para continuar.',
    // Sprint stdout-transport-replay (D013-EC-118) — copy visible y
    // clara para pérdida de transporte verificada / truncado. Sin
    // jerga: causa en claro + qué hacer a continuación.
    outputLost:
      'Parte de la respuesta del agente se perdió por un corte de red — pídele al agente que repita su última respuesta',
    outputTruncated:
      'Un mensaje del agente era demasiado grande para entregarse — pídele que lo reenvíe en partes más pequeñas',
    // Sprint compaction-visibility (D1) — FALLBACK solamente; el backend
    // escribe su propia frase (`message`, español) en cada fila
    // `compaction_failed` y la píldora la renderiza tal cual
    // (`persistedMessage()`). Gemelo de `COMPACTION_FAILED_FOUNDER_MESSAGE`
    // en `backend/src/claude/session-manager.ts` — mantener sincronizados.
    compactionFailed: 'No se pudo compactar la conversación.',
  },

  // ── Error card ─────────────────────────────────────────────────────
  error: {
    agentFailed: 'Agente falló',
    agentFailedWithReason: 'Agente falló: {reason}',
  },

  // ── Errores de spawn (Sprint S20.5 PR-3 / D013-EC-69) ─────────────
  // Copy founder-mode para el toast cuando `POST /api/agents` (o el
  // spawn lazy en el primer mensaje) es rechazado por el backend con
  // un `code` estructurado. Cada rama dice al founder QUÉ hacer en
  // lenguaje claro; el fallback genérico preserva el texto legacy.
  errors: {
    spawn: {
      NO_RUNNER_AVAILABLE:
        'No hay servidores disponibles. Abre Ajustes \u2192 Servidores para ver el estado.',
      RUNNER_NOT_READY:
        'El servidor está iniciando. Reintenta en unos segundos.',
      CLAUDE_NOT_AUTHENTICATED:
        'Claude no está autenticado en este servidor. Pide al operador que ejecute `claude /login` en el servidor.',
      RUNNER_OFFLINE:
        'El servidor de este agente está offline. Reintenta o elige otro servidor.',
      GENERIC: 'No se pudo crear la conversación. Toca para reintentar.',
    },
  },

  // ── Banners de estado del agente ────────────────────────────────
  agentBanner: {
    stuckTitle: 'Sin actividad en {duration}',
    stuckWait: 'Esperar',
    stuckStop: 'Parar',
    errorTitle: 'Agente detenido después de {reason}',
    errorRetry: 'Reintentar',
    errorFork: 'Bifurcar desde el último turno OK',
    interruptedTitle: 'Detenido después de {duration}',
    interruptedResume: 'Reanudar',
    interruptedFork: 'Bifurcar',
  },

  // ── Banners de red / sistema ────────────────────────────────────
  networkBanner: {
    reconnecting: 'Reconectando',
    disconnected: 'Desconectado — los agentes siguen corriendo en el servidor',
    disconnectedRetry: 'Reintentar',
  },

  // Sprint 20.3 PR-5 — auto-clone surface (failure title only; the
  // body string is shipped from the backend already in the founder's
  // language preference, so we never re-translate it here).
  spawnPreparingToast: {
    failedTitle: 'No se pudo preparar el proyecto',
    retry: 'Reintentar',
    dismissAria: 'Cerrar notificación',
    dismissGlyph: '×',
  },

  // ── Banner runner no disponible ───────────────────────────────────
  // S20.3 PR-6 (variante offline) + auto-failover-on-rate-limit
  // (D013-EC-109, variante rate-limited). Mismo patrón de banner, dos
  // copys discriminadas. Founder-mode: lenguaje plano — nunca
  // "rate-limit" ni "weekly cap" en la superficie de usuario.
  runnerUnavailableBanner: {
    offline: {
      title: '{runner} está offline',
      subtitle: 'El runner de este agente no responde. Reasigna a otro runner para continuar.',
      tryAnotherRunner: 'Probar otro runner',
    },
    rateLimited: {
      title: '{runner} agotó la cuota semanal',
      subtitle:
        'El runner de este agente se quedó sin cuota semanal. Mueve esta conversación a otro runner para continuar.',
      tryAnotherRunner: 'Mover a otro runner',
    },
    busyLabel: 'Reasignando…',
    dismissAria: 'Cerrar aviso',
    dismissGlyph: '×',
    successToast: 'Reasignado a {to}.',
    errorToast: 'No se pudo reasignar: {reason}',
    genericError: 'No se pudo reasignar.',
  },

  // Sprint 20.5 PR-2 (D013-EC-68) — runner could not resume the
  // Claude child to forward a queued message. Founder voice: calm,
  // declarative, with a next action.
  sessionLostBanner: {
    title: 'Esta sesión terminó',
    subtitle: 'Pulsa retry para reanudar la conversación.',
    retry: 'Retry',
    busyLabel: 'Reanudando…',
    dismissAria: 'Cerrar aviso de sesión terminada',
    dismissGlyph: '×',
    successToast: 'Conversación reanudada.',
    errorToast: 'No se pudo reanudar: {reason}',
    genericError: 'No se pudo reanudar.',
  },

  // Sprint 20.6 PR-2 (D013-EC-72) — Anthropic descartó la sesión.
  // Reanudar es imposible por definición; la siguiente acción
  // founder es lanzar un agente nuevo (los turnos previos siguen
  // visibles, persistidos en el dashboard).
  sessionExpiredBanner: {
    title: 'Esta sesión expiró',
    subtitle: 'Anthropic descartó el contexto de la conversación. Lanza un agente nuevo para continuar.',
    newAgent: 'Lanzar agente nuevo',
    dismissAria: 'Cerrar aviso de sesión expirada',
    dismissGlyph: '×',
  },

  // Sprint 20.6 PR-2 (D013-EC-72) — toast no-bloqueante de 2 s tras
  // una respawn-on-startup exitosa. Le aclara al usuario que el
  // parpadeo fue intencional.
  respawnedToast: {
    message: 'Conversación reanudada',
  },

  // ── Live activity (Sprint 17) ──────────────────────────────────────
  liveActivity: {
    thinking: 'Pensando',
    working: 'Trabajando',
    // Sprint compaction-visibility (D4/D13) — mismo contenedor que
    // 'thinking': puntos pulsantes + tiempo transcurrido.
    compacting: 'Compactando contexto…',
    ariaLabel: 'Actividad del agente',
    // Sprint d028-recursive-liveness PR-C (D5, EC-176) — prefijo para la
    // fila 'tool' cuando la actividad pertenece a un subagente (hijo), no
    // a la conversación misma. `{label}` es la misma cadena de
    // `tool_use.input.description` que ya usa la fila propia del padre —
    // ninguna cadena de fallback NUEVA. MATIZADO, Gate 2 MAJOR 2: esa
    // reutilización no garantiza que `{label}` esté siempre libre de
    // nombres de herramienta — la rama `default:` de `fallbackHeadline`
    // (`headline.ts`) todavía titula un nombre de herramienta no
    // reconocido cuando no hay descripción, preexistente en la fila
    // propia del padre y heredado aquí sin cambios (no corregido,
    // `headline.ts` queda fuera del alcance de este PR — D013-EC-214).
    subagentLabel: 'Subagente: {label}',
  },

  // ── Sprint working-signal-ssot — subtítulos de trabajo por motivo ──
  workingReason: {
    working: 'Trabajando…',
    silent: 'Sigue trabajando — un rato sin salida',
    awaitingPermission: 'Esperando tu permiso',
    awaitingTask: 'Esperando a un agente en segundo plano',
    awaitingWakeup: 'Esperando una comprobación programada',
    awaitingWakeupAt: 'Esperando una comprobación programada · {time}',
    // Sprint conditional-wakeups PR-A (D7/D8) — cuando la cadena de envío
    // está bloqueada (attempt_count > 0 Y last_error presente), esta
    // línea REEMPLAZA el prefijo genérico "esperando..." en vez de
    // añadirse a él (decisión de diseño post-hallazgo de truncamiento:
    // el estado bloqueado es estrictamente más informativo que "esperando
    // una comprobación programada"; liderar con la anomalía es
    // founder-first §2 prioridad 4 — "¿está bloqueado o preguntando
    // algo?"). Humanizado, nunca el código crudo (founder-first §8.6).
    // PR-B — reviewer note: el tiempo final se etiqueta como "prevista"
    // (no una hora sin contexto). **Solo para filas TIMED** — ahí el
    // tiempo ES una hora programada de reanudación. Una fila CONDITIONAL
    // bloqueada usa `wakeupBlockedLimitAt` (abajo): M7 prohíbe etiquetar
    // el deadline como una hora prevista.
    wakeupBlockedAt: 'Aviso programado bloqueado: {reason} · intento {attempt} · prevista {time}',
    wakeupBlocked: 'Aviso programado bloqueado: {reason} · intento {attempt}',
    // Sprint conditional-wakeups PR-B Gate-2 fix (M7 BLOCKER) — la fila
    // CONDITIONAL bloqueada tiene su propia clave: el tiempo es
    // `wakeupDeadlineAt`, nunca `wakeupFireAt`, y se etiqueta "límite"
    // (nunca "prevista" — eso implicaría una hora de reanudación
    // programada, que M7 prohíbe para el canal condicional).
    wakeupBlockedLimitAt: 'Aviso bloqueado: {reason} · intento {attempt} · límite {time}',
    blockedReason: {
      runnerOffline: 'servidor no disponible',
      runnerRateLimited: 'cuota semanal agotada',
      capacityExhausted: 'sin capacidad',
      default: 'reintentando',
    },
    // Sprint conditional-wakeups PR-B (spec D8, M7) — fila condicional
    // (asoasis-wake). "límite" en vez de "prevista"/"resumes at": el
    // deadline es un límite, nunca una hora de reanudación. `note` es la
    // ÚNICA cadena relacionada con la sonda que se muestra (nunca
    // probe_cmd, nunca un código crudo).
    awaitingConditionNote: 'esperando: {note} · límite {time}',
  },

  // ── Todo checklist ─────────────────────────────────────────────────
  todos: {
    heading: 'Todos',
    empty: 'TodoWrite (sin elementos)',
    progress: '{completed}/{total} listos',
    cancelled: '{count} cancelados',
  },

  // ── Plan mode banner ───────────────────────────────────────────────
  planMode: {
    banner: 'Modo Plan — solo investigación, sin cambios en archivos',
  },

  // ── Subagent panel ─────────────────────────────────────────────────
  subagent: {
    label: 'Subagente',
    tools: '{count} herramienta',
    toolsPlural: '{count} herramientas',
    running: '(trabajando…)',
    error: '(falló)',
    prompt: 'Prompt',
    subagentError: 'Error del subagente',
    finalResult: 'Resultado final',
    emptyResult: '(vacío)',
    // Sprint subagent-panel-ux (D-sap-3) — etiquetas founder para el
    // prompt enviado al subagente y su respuesta, en prosa legible.
    promptFounder: 'Se le pidió:',
    resultFounder: 'Devolvió:',
    // Sprint ai-elements-rendering — árbol recursivo de subagentes.
    depthBadge: 'N{depth}',
    descendantTools: '(+{count})',
    orphanNotice: 'Salida de subagente sin su llamada padre (historial truncado)',
    // Sprint subagent-launch-ack-liveness (D4, D013-EC-659) — el cuarto estado de la tarjeta:
    // nunca llegó una confirmación de fin (26% de D013-EC-169), se cruzó la cota de edad (B3),
    // o el padre está genuinamente parado. Nunca "terminado".
    unconfirmed: '(sin confirmación de fin)',
    // Sprint subagent-launch-ack-liveness (D4, ítem 8, §7b C10) — etiqueta de capa developer
    // para el recibo crudo del lanzamiento en segundo plano, mostrado junto al (o en vez del)
    // resumen real de fin — nunca el cuerpo de resultado founder.
    launchAck: 'Recibo de lanzamiento (crudo)',
    // Directiva del founder 2026-09-13 (aprobación de maqueta): aria-label del check verde que
    // se pinta cuando el subagente ha terminado sin error — el equivalente de `role="status"`
    // del spinner, pero para el estado asentado.
    done: 'terminado',
  },

  // ── Tarjeta de trabajador despachado ───────────────────────────────
  // Sprint dispatch-lineage-ui (D4b) — un `/dispatch` del caller, pintado
  // con el mismo componente que un subagente. Los estados mapean los
  // terminales del ledger `dispatches` (D6b de owner-dispatch).
  dispatchCard: {
    label: 'Trabajador',
    working: '(trabajando…)',
    quiet: '(quieto)',
    reporting: '(terminó · reportando)',
    reported: '(reportó)',
    failed: '(falló)',
    deadline: '(plazo vencido)',
    needsFounder: '(necesita tu aprobación)',
    noStart: '(no arrancó)',
    loading: 'Cargando la transcripción…',
    unknownCaller: 'otro agente',
  },

  // ── Panel de workflow ──────────────────────────────────────────────
  // Sprint d028-workflow-agents (D2) — los N agentes internos de un
  // Workflow. El sustantivo del encabezado reutiliza la humanización
  // EXISTENTE de `Workflow` (verb-map.ts/headline.ts
  // `HARNESS_TOOL_COPY.Workflow`), nunca el identificador crudo
  // (D013-EC-214).
  workflowPanel: {
    header: 'Ejecutó un workflow',
    agents: '{count} agente',
    agentsPlural: '{count} agentes',
    running: '(trabajando…)',
    done: '(listo)',
    interrupted: '(interrumpido)',
    noPreview: 'Sin detalles disponibles todavía.',
  },

  // ── Dual mode chip ─────────────────────────────────────────────────
  dualMode: {
    label: 'Modo desarrollador',
    disableTooltip: 'Desactivar modo desarrollador',
  },

  // ── Permisos ───────────────────────────────────────────────────────
  permissions: {
    agentWantsTo: '{agent} quiere {action}',
    allowOnce: 'Permitir una vez',
    allowAlways: 'Permitir siempre',
    deny: 'Denegar',
    deleteFolderNamed: '{agent} quiere eliminar la carpeta {name}',
    deleteFolder: '{agent} quiere eliminar una carpeta',
    deleteFileNamed: '{agent} quiere eliminar {name}',
    deleteFiles: '{agent} quiere eliminar archivos',
    pushToGithub: '{agent} quiere hacer push a GitHub',
    discardChanges: '{agent} quiere descartar cambios locales',
    changePermissions: '{agent} quiere cambiar permisos de archivo',
    changeOwnership: '{agent} quiere cambiar propietario del archivo',
    runAsAdmin: '{agent} quiere ejecutar un comando como administrador',
    downloadFromHost: '{agent} quiere descargar de {host}',
    downloadFromInternet: '{agent} quiere descargar de internet',
    installPackage: '{agent} quiere instalar {package}',
    stopProcess: '{agent} quiere detener un proceso en ejecución',
    runCommand: '{agent} quiere ejecutar un comando',
    modifyFileNamed: '{agent} quiere modificar {name}',
    modifyFile: '{agent} quiere modificar un archivo',
    fetchFromHost: '{agent} quiere obtener datos de {host}',
    fetchFromInternet: '{agent} quiere obtener datos de internet',
    searchWeb: '{agent} quiere buscar en la web',
    finishedPlanning: '{agent} terminó de planificar y quiere comenzar',
    sensitiveAction: '{agent} quiere ejecutar una acción sensible',
    insideDir: 'Dentro de {dir}.',
    pushDetails: 'Push {branch} a {remote}.',
    hardResetDetails: 'Hard-reset a {ref}. Cualquier cambio no committed se perderá.',
    forceCheckoutDetails: 'Force-checkout — cualquier cambio no committed se perderá.',
    sudoDetails: 'sudo eleva privilegios en todo el sistema.',
    usingTool: 'Usando {tool}.',
    openDevDetails: 'Abre los detalles técnicos para ver el comando exacto.',
    commandStartsWith: 'Empieza con {verb}. Abre los detalles técnicos para ver el comando exacto.',
    planToAgentBody: 'Aprobar para cambiar de modo Plan a modo Agente para el resto de este turno.',
    toolLabel: 'Herramienta: {tool}. Abre los detalles técnicos para ver la solicitud exacta.',
    showRawDetail: 'Mostrar lo que Claude va a ejecutar',
    askingPermission: 'Claude está pidiendo permiso',
    addsGlobalRule: 'Agrega una regla global: {rule}',
    couldNotSave: 'No se pudo guardar tu decisión',
    requestGone: 'Esta solicitud ya fue resuelta.',
    couldNotSaveDetail: 'No se pudo guardar tu decisión: {error}',
    allowAlwaysFor: 'Permitir siempre para',
  },

  // ── Error boundary ─────────────────────────────────────────────────
  errorBoundary: {
    title: 'Algo se rompió',
    body: 'El dashboard encontró un error inesperado y dejó de renderizar. Tus agentes en el servidor no se ven afectados — esto es solo la vista del navegador.',
    reload: 'Recargar',
    copyDetails: 'Copiar detalles',
    crashReport: 'Informe de error de As Oasis',
  },

  // ── Protected route ────────────────────────────────────────────────
  protectedRoute: {
    loading: 'Cargando dashboard…',
  },

  // ── Autocomplete dropdown ──────────────────────────────────────────
  autocomplete: {
    noResults: 'Sin resultados',
    loading: 'Buscando…',
    error: 'No se pudieron cargar sugerencias',
  },

  // ── Compartido / común ─────────────────────────────────────────────
  common: {
    cancel: 'Cancelar',
    retry: 'Reintentar',
    close: 'Cerrar',
    save: 'Guardar',
    edit: 'Editar',
    resend: 'Reenviar',
    searchNoMatch: 'No hay agentes con "{query}"',
    refresh: 'Actualizar',
  },

  // ── Tooltips ───────────────────────────────────────────────────────
  tooltip: {
    newAgent: 'Nuevo agente',
    searchAgents: 'Buscar agentes',
    archive: 'Archivar',
    restore: 'Restaurar — devolver a la lista activa',
    deletePermanently: 'Eliminar permanentemente',
    moreActions: 'Más acciones',
    stopAgent: 'Parar agente',
    notifications: 'Notificaciones',
    settings: 'Ajustes',
    account: 'Cuenta',
  },

  // ── Sticky / user message ──────────────────────────────────────────
  message: {
    edit: 'Editar',
    resend: 'Reenviar',
    stamp: { today: 'Hoy', yesterday: 'Ayer' },
    // Sprint agent-message-attribution (D3) — etiquetas del chip de origen.
    origin: {
      system: 'Sistema',
      wakeup: 'Despertador',
      // Fallback cuando el agente emisor no está en el store en vivo
      // (borrado/interno) — `id` es el id corto de 8 chars (D4).
      agentFallback: 'Agente {id}',
    },
    // Sprint machine-message-collapse (D3, D8) — pista al pasar el ratón por
    // el marcador plegado; el texto está a un clic.
    collapsed: {
      hint: 'Clic para leer el mensaje',
    },
  },

  // ── Campaña agent-noise-collapse — fila plegada de informe interno ──
  // Founder-first §3: el título + el resumen son la capa founder; el
  // cuerpo crudo vive detrás del expansor.
  internalReport: {
    helperNamed: 'Informe interno de {name}',
    helperAnonymous: 'Informe interno de un ayudante',
    system: 'Aviso del sistema',
  },

  // ── Scroll to bottom ───────────────────────────────────────────────
  scrollToBottom: {
    label: 'Ir al final',
    newMessage: '{count} mensaje nuevo',
    newMessages: '{count} mensajes nuevos',
    ariaLabel: '{count} mensajes nuevos, ir al final',
  },

  // ── Regla de mensajes del founder (sprint chatgpt-message-rail, D10) ──
  messageRail: {
    label: 'Índice de tus mensajes',
    listTitle: 'Tus mensajes',
    attachmentOnly: '[adjunto]',
    current: 'Estás aquí',
  },

  // ── Vista de chat ─────────────────────────────────────────────────
  chatView: {
    loadingMessages: 'Cargando mensajes…',
    couldNotLoadMessages: 'No se pudieron cargar los mensajes: {error}',
    noMessagesYet: 'Aún no hay mensajes — el agente responderá pronto.',
    loadingOlder: 'Cargando historial anterior…',
  },

  // ── Panel de conversación (sprint fc-conversation-panel, §2) ───────
  conversationPanel: {
    title: 'Detalles de la conversación',
    expand: 'Mostrar el panel de la conversación',
    collapse: 'Ocultar el panel de la conversación',
    campaign: {
      title: 'Campaña, sprint y fase',
      body: 'Todavía no hay de dónde sacarlo. Lo trae la campaña aos-harness-runtime, que va a llevar la cuenta de en qué campaña, sprint y fase va cada conversación.',
    },
    documents: {
      title: 'Documentos y enlaces',
      loading: 'Cargando…',
      partialWindow: 'Desde el tramo que tienes cargado. Sube en la conversación para incluir lo anterior.',
      docsLabel: 'Documentos',
      linksLabel: 'Enlaces',
      noneYet: 'Nada todavía.',
      more: '+{count} más',
    },
    progress: {
      title: 'Progreso',
      noPlanYet: 'Este agente aún no ha escrito un plan.',
      readout: '{current} de {total}',
    },
    summary: {
      title: 'Resumen',
      body: 'Todavía no hay de dónde sacarlo. Lo trae la campaña aos-observability, que va a guardar y resumir las conversaciones.',
    },
  },

  // ── Búsqueda en la conversación (sprint fc-conversation-search, §2) ─
  // El corpus es `user` + `assistant_text` únicamente (§2.1) — "lo que se
  // dijo", nunca actividad de herramientas. Sin ruta de backend (§10.2); la
  // mitad entre conversaciones se nombra, no se construye (§2.5, propiedad
  // de la campaña aos-observability — nombre propio, nunca se traduce,
  // comms.md §Nombres propios).
  conversationSearch: {
    trigger: 'Buscar en esta conversación',
    close: 'Cerrar búsqueda',
    previous: 'Resultado anterior',
    next: 'Resultado siguiente',
    matchCount: '{current} de {total}',
    scopeHint: 'Busca en lo que tú y el agente habéis escrito.',
    scopePartial:
      'Buscado en los {count} mensajes de esta conversación que ya tienes cargados. Queda conversación anterior sin cargar.',
    loadOlder: 'Cargar 200 mensajes anteriores y buscar otra vez',
    loadOlderFailed: 'No se pudo cargar lo anterior. Inténtalo otra vez.',
    noResults: 'Sin resultados en lo que hay cargado.',
    fleetWidePending: 'Buscar en todas las conversaciones llega con la campaña aos-observability.',
    moreResults: '+{count} más',
    loading: 'Cargando la conversación…',
    loadError: 'No se pudo cargar la conversación.',
    authorYou: 'Tú',
  },

  // ── Message bubble ────────────────────────────────────────────────
  messageBubble: {
    empty: '(vacío)',
    toolError: 'Error de herramienta',
    toolResult: 'Resultado de herramienta',
  },

  // ── Indicador parcial ─────────────────────────────────────────────
  partialIndicator: {
    ariaLabel: 'El agente está escribiendo',
  },

  // Sprint 20.3 PR-7 (D013-EC-22) — píldora de stream truncado.
  streamTruncated: {
    label: '[truncado · recarga para refrescar]',
    ariaLabel: 'Stream en directo truncado — haz clic para recargar desde el servidor',
    icon: '⚠',
  },

  // ── Barra de búsqueda ─────────────────────────────────────────────
  searchBar: {
    placeholder: 'Buscar agentes…',
    ariaLabel: 'Buscar agentes',
    clearAriaLabel: 'Limpiar búsqueda',
  },

  // ── Spinner ───────────────────────────────────────────────────────
  spinner: {
    ariaLabel: 'Cargando',
  },

  // ── Menú contextual extra ─────────────────────────────────────────
  agentActions: {
    ariaLabel: 'Acciones del agente',
  },

  // ── Permission card extra ─────────────────────────────────────────
  permissionCard: {
    ariaLabel: 'El agente pide permiso',
    showDevDetails: 'Ver lo que el agente está por ejecutar',
  },

  // ── Tarjeta de agregación ─────────────────────────────────────────────
  aggregation: {
    running: 'ejecutando',
    errored: '{count} con error',
    completed: 'completado',
  },

  // ── Tarjeta de resumen de turno ───────────────────────────────────────
  turnSummary: {
    errored: 'Con errores',
    stopped: 'Parado',
    typecheck: 'Tipos',
    tests: 'Tests',
    lint: 'Estilo',
    build: 'Compilación',
    failed: 'falló',
    // Sprint turn-verdict-line (D3) — metric translation, singular/plural
    // pairs. `parseMetric` singulariza explícitamente (`1 failed`,
    // `1 passed`), así que una sola clave por forma renderizaría
    // "1 errores" / "1 avisos" / "1 fallaron".
    metricFailedOne: '1 falló',
    metricFailedMany: '{count} fallaron',
    metricPassedOne: '1 pasó',
    metricPassedMany: '{count} pasaron',
    metricErrorsOne: '1 error',
    metricErrorsMany: '{count} errores',
    metricWarningsOne: '1 aviso',
    metricWarningsMany: '{count} avisos',
    // Sprint turn-end-signals-line (D2, TES2) — always a count, never a
    // tool name (`founder-first-vision.md` §8.2: `Bash`/`Write` is
    // jargon). The per-denial detail already ships in developer mode.
    blockedOne: '1 acción bloqueada',
    blockedMany: '{count} acciones bloqueadas',
  },

  // ── Filas de acciones compactadas ─────────────────────────────────────
  actionRow: {
    durationSeconds: '{s}s',
    durationMinutes: '{m}m {s}s',
    durationHours: '{h}h {m}m',
    countActions: '{count} acciones',
    failedOne: '1 falló',
    failedMany: '{count} fallaron',
  },

  // ── Cabecera de turno colapsado ───────────────────────────────────────
  turnCollapse: {
    worked: 'Trabajó',
    workedForSeconds: 'Trabajó {s}s',
    workedForMinutes: 'Trabajó {m}m {s}s',
    workedForHours: 'Trabajó {h}h {m}m',
  },

  // ── Cabecera de segmento de trabajo (sprint work-segment-collapse, D3) ──
  workSegment: {
    header: '{worked} · {time}',
    ariaLabel: 'Trabajo hecho entre dos mensajes',
    // Sprint subagent-launch-ack-liveness (D6) — un lanzamiento en segundo plano dentro del
    // segmento sin fin confirmado: la cabecera dice DESDE, nunca "Worked for" (eso afirmaría
    // una duración terminada que este segmento no tiene).
    headerLive: 'Trabajando desde {time}',
  },

  // ── Barra de progreso fija ────────────────────────────────────────────
  progress: {
    allDone: 'Todos los pasos listos',
    working: 'Trabajando…',
    phaseOf: 'Fase {current} de {total}',
  },

  // ── Pill de actualización de todo ─────────────────────────────────────
  todoUpdate: {
    started: 'Iniciado',
    completed: 'Completado',
    cancelled: 'Cancelado',
  },

  // ── Tarjeta de pregunta al usuario ────────────────────────────────────
  askQuestion: {
    heading: 'Claude necesita tu input',
    pickOption: 'Elige una opción abajo para continuar.',
    answerQuestions: 'Responde {count} preguntas para continuar.',
    submit: 'Enviar',
    pickAny: '(elige varias)',
    noOptions: 'No se proporcionaron opciones.',
    askingQuestion: 'Claude está haciendo una pregunta',
    couldNotSend: 'No se pudo enviar tu respuesta',
    alreadyAnswered: 'Esta pregunta ya fue respondida.',
    couldNotSendDetail: 'No se pudo enviar tu respuesta: {error}',
  },

  // ── Pill de resultado de permiso ──────────────────────────────────────
  permissionOutcome: {
    autoAllowed: 'Auto-permitido:',
    allowed: 'Permitido:',
    denied: 'Denegado:',
    // D013-EC-197 (sprint e97-permission-window) D5/D6.
    expired: 'Expiró:',
    expiredAllowAlwaysHint:
      'La herramienta ya se ejecutó sin tu respuesta. Permitir siempre aplica a la próxima vez.',
    decidedBy: 'Decidido por:',
    reason: 'Razón:',
    allowlistPattern: 'Patrón de lista blanca:',
    tool: 'Herramienta:',
  },

  // ── Elemento de lista de agentes ──────────────────────────────────────
  agentList: {
    working: 'Trabajando…',
    // Sprint dispatch-lineage-ui (D2) — insignia de una sesión despachada
    // (solo alcanzable por búsqueda explícita).
    dispatchedBy: 'Despachada por {name}',
    waitingForInput: 'Esperando respuesta',
    noActivity: 'Sin actividad en {min}m',
    done: 'Listo',
    diffStat: '+{added} / -{removed} · {files} archivos',
    diffStatNoRemoved: '+{added} · {files} archivos',
    unread: 'No leído',
    renameAgent: 'Renombrar agente',
  },

  // ── Chip de modo ──────────────────────────────────────────────────────
  modeChip: {
    agent: 'Autónomo',
    plan: 'Plan',
    auto: 'Autónomo',
    agentDesc: 'Ejecuta sin pedir permiso. Las operaciones peligrosas se bloquean automáticamente.',
    planDesc: 'Solo lectura — explora y planifica sin modificar archivos',
    autoDesc: 'Ejecuta sin pedir permiso. Las operaciones peligrosas se bloquean automáticamente.',
    modeAriaLabel: 'Modo de permisos: {mode}. Haz clic para cambiar.',
  },

  // ── Chips de modelo/esfuerzo ──────────────────────────────────────────
  modelChip: {
    modelAndEffortAriaLabel: 'Modelo: {model}, esfuerzo: {effort}. Haz clic para cambiar.',
    popoverAriaLabel: 'Elige modelo y esfuerzo',
    opusOnlyTag: 'Solo Opus',
  },

  // ── Chip de rama ──────────────────────────────────────────────────────
  branch: {
    ahead: '{count} adelante',
    behind: '{count} atrás',
    aheadOfMain: '{count} adelante de main',
    behindOfMain: '{count} atrás de main',
    uncommittedChanges: 'cambios sin commit',
  },

  // ── Chip del runner (Sprint 20.3 PR-9 / D20 / UC-38) ──────────────────
  runnerChip: {
    tooltipFull: '{hostname} · {account} · {cap}',
    tooltipNoCap: '{hostname} · {account}',
    tooltipMinimal: '{runner} · uso no reportado',
    cap: 'usado {value}%',
    accountUnknown: 'cuenta no reportada',
  },

  // ── Donut de contexto ─────────────────────────────────────────────────
  contextDonut: {
    ariaLabel: 'Contexto usado {pct}%',
    defaultTooltip: 'Contexto usado: {pct}%\nMedido en la última respuesta del modelo.',
    // Sprint compaction-visibility (D14) — usada cuando Composer.tsx conoce
    // los tokens reales (`agent.contextUsedTokens`/`contextWindowTokens`);
    // en caso contrario (incl. justo tras un compact_boundary, D8) cae al
    // `defaultTooltip` genérico de arriba.
    tooltipWithTokens: 'Contexto usado: {used} / {max} tokens ({pct}%)\nMedido en la última respuesta del modelo.',
  },

  // ── Divisor de compactación (Sprint compaction-visibility, D11/D13) ───
  // El resumen del popover (`popoverTitle` en adelante, cuerpo aparte) es
  // el ÚNICO texto founder-facing de este bloque que se muestra en inglés
  // verbatim — excepción declarada a `founder-alignment.md` §Communication
  // (D11, R1 change 7): el resumen lo escribe el CLI, no As Oasis, y una
  // traducción fabricada sería peor que la evidencia literal.
  compaction: {
    dividerLabel: 'Conversación compactada · ~{freed} tokens liberados',
    popoverTitle: 'Lo que el agente recuerda de antes',
    popoverMetaAuto: 'Compactación automática · {pre} → {post} tokens · {duration}',
    popoverMetaManual: 'Compactación manual · {pre} → {post} tokens · {duration}',
    // Trigger desconocido/futuro (D2: el trigger pasa como string opaco).
    popoverMeta: 'Compactación · {pre} → {post} tokens · {duration}',
    noSummary: 'No se guardó el resumen de esta compactación.',
  },

  // ── Modal de uso ─────────────────────────────────────────────────────
  // Sprint 20.3 PR-10 — repensado de agregado por cuenta OAuth a
  // agregado por runner sobre toda la topología. D17.
  // ── Pill de aprovisionamiento de proyecto (Sprint add-project-autoprovision) ──
  // El copy de fallo lo posee el backend (founderCopyForProvisionFailure)
  // y se renderiza tal cual; aquí solo la etiqueta en curso y el fallback.
  projectProvisioning: {
    preparing: 'Preparando proyecto…',
    failedFallback: 'La configuración del proyecto falló. El primer agente lo reintentará automáticamente.',
  },

  runnersHealth: {
    ariaLabel: 'Estado de runners — click para ver detalles de capacidad',
    allHealthy: '{count} runners',
    allHealthyWithCap: '{count} runners · {cap}',
    singleRunner: '1 runner',
    singleRunnerWithCap: '1 runner · {cap}',
    degraded: '{online}/{total} runners',
    allOffline: 'Sin runners online',
    cap: {
      percent: '{value}% libre',
    },
    tooltip: {
      title: 'Capacidad de runners',
      capFree: '{value}% libre',
      capUnknown: 'capacidad desconocida',
      // Sprint runner-quarantine-truth (D3, D013-EC-126) — palabra de
      // estado por runner; el "no disponible" ×12 no daba señal alguna
      // durante el incidente de cuarentena. Cuarentena humaniza además
      // el motivo (grupo `reason`).
      stateOffline: 'desconectado',
      stateDraining: 'en drenado',
      empty: 'Aún no hay runners registrados.',
    },
    // Etiquetas founder-mode por cada `RunnerUnavailableReason`. Nunca
    // se expone el enum (founder-first §5 — sin jerga); el detalle
    // técnico vive en Ajustes → Servidores. Vocabulario espejado de
    // `renderUnavailableAlert` (backend/src/api/runner-ws.ts),
    // traducido a lenguaje founder.
    reason: {
      missingCredential: 'credencial en revisión',
      claudeAccountBlocked: 'cuenta de Claude bloqueada',
      claudeNotAuthenticated: 'falta iniciar sesión en Claude',
      diskFull: 'disco lleno',
      wrongWorkspace: 'problema con la credencial',
      incompatibleVersion: 'necesita actualización',
    },
  },

  usage: {
    title: 'Uso de runners',
    description: 'Capacidad y utilización de cada runner en el despliegue.',
    loading: 'Consultando uso…',
    error: 'No se pudo obtener el uso. Inténtalo en unos minutos.',
    // Sprint usage-last-known-good (D013-EC-172) D2 — abrir el modal ya no
    // fuerza una llamada mientras hay una ventana de espera activa, así que
    // la vía de escape es explícita y dice con quién habla.
    retryNow: 'Reintentar ahora',
    retryNowHint: 'Pregunta a Anthropic ahora mismo',
    // D4 — cuál de los dos silencios es. La respuesta a "¿nos sigue
    // limitando Anthropic o simplemente no estamos preguntando?", sin
    // gastar una llamada. Línea discreta DEBAJO de los números, nunca una
    // tarjeta que los sustituya.
    lastAttemptNotAsked: 'Sin consultar hasta las {time} (Anthropic nos pidió esperar)',
    lastAttemptFailedRetryAt: 'Anthropic nos limitó al intentarlo · reintento a las {time}',
    lastAttemptFailed: 'No se pudo actualizar esta lectura en el último intento',
    // Sprint usage-from-stream (D4, change 9b) — sustituye la nota de
    // último intento para una fila cuyo uso de sesión/semanal viene del
    // stream de los agentes mientras su propia llamada de uso está
    // muerta: la tarjeta ámbar de error desaparece (los números SÍ se
    // ven), así que esta es la única traza visible de una llamada
    // muerta hasta que salga la alerta de EC-222.
    // Sprint usage-fable-window-from-stream (D4 iv) — nunca nombra una
    // barra de Fable que puede estar ausente en esta fila (NB-6): una
    // fila solo-stream puede o no tener aún un turno tipo Fable.
    streamOnlyNote:
      'Lecturas desde el stream de los agentes; Sonnet/Opus no disponibles',
    empty: 'No hay runners registrados todavía.',
    colRunner: 'Runner',
    colAccount: 'Cuenta',
    colCap: 'Usado · 5h',
    colWeeklySonnet: 'Sonnet · esta semana',
    colWeeklyOpus: 'Opus · esta semana',
    colActive: 'Activos',
    colStatus: 'Estado',
    colLastSeen: 'Visto por última vez',
    // Etiquetas de estado — una por estado de `RunnerAvailability`
    // (sprint runner-quarantine-truth D4). Antes el modal proyectaba el
    // `status` crudo del WS: decía "ONLINE" de runners en cuarentena
    // mientras el banner decía "offline" de la misma flota (EC-126).
    statusOnline: 'Online',
    statusOffline: 'Offline',
    statusDraining: 'Drenando',
    statusQuarantined: 'En cuarentena',
    footerEarliestReset: 'Próximo reset: {hostname} en {time}',
    footerNoReset: 'Ningún cap reset reportado todavía.',
    // Sprint runner-claude-version-visibility — versión de Claude Code por
    // runner + etiqueta cuando el runner va por detrás del máximo de la flota.
    claudeVersionLabel: 'Claude Code',
    versionOutdated: 'desactualizado',
    // Sprint usage-single-emitter (D013-EC-166) D3 — la edad del dato,
    // visible en todas las pantallas de uso. Tras este sprint casi todas
    // las lecturas vienen de la caché del control plane a propósito, así
    // que Santi tiene que poder distinguir un dato vivo de uno viejo.
    // Pasados 30 min la copia deja de insinuar frescura y lo dice.
    ageJustNow: 'Actualizado ahora mismo',
    ageMinutes: 'Actualizado hace {age}',
    ageStale: 'Actualizado hace {age} — puede estar desfasado',
    ageUnknown: 'Sin lectura de uso todavía',
    // Sprint usage-from-stream (D4) — fallback corto para el hueco
    // `{age}` de `noReadingSinceReset` cuando no hay marca de tiempo por
    // ventana (un bucket sin procedencia propia, p. ej. `extra_usage` —
    // siempre por RPC). Deliberadamente NO `ageUnknown`: esa es una
    // frase completa y no encaja en la gramática "hace {age}".
    ageUnknownShort: 'un tiempo indeterminado',
    // Sprint usage-from-stream (D4) — un bucket cuya ventana se
    // REINICIÓ sin lecturas nuevas desde entonces. Solo de renderizado:
    // nunca un 0 % numérico (el miedo de Santi va en sentido contrario —
    // una lectura vieja solo puede SOBREESTIMAR el uso, nunca
    // subestimarlo, pero tras un reinicio de ventana la verdad es
    // genuinamente desconocida hasta el siguiente turno del agente).
    noReadingSinceReset: 'Ventana reiniciada — sin lecturas desde entonces (última hace {age})',
    percent: '{value}%',
    resetsIn: 'Reset en {time}',
    capUnknown: '—',
    hoursOfLimit: '{value}h / {limit}h',
    // Sprint 20.8 (D013-EC-76) — etiquetas de las cinco barras canónicas
    // que devuelve Anthropic. Misma terminología que Claude Code en VS
    // Code (paridad con Cursor por cursor-ux-mirror.md).
    session5h: 'Sesión (5h)',
    weekly7d: 'Semanal (7d)',
    // Sprint usage-fable-window-from-stream (D4) — ventana semanal
    // "Fable" (`seven_day_overage_included`), origen stream. La
    // etiqueta dice "Fable"; la clave del wire sigue siendo la del
    // proveedor (spec §1/F3).
    weeklyFable: 'Semanal · Fable',
    weeklySonnet: 'Sonnet semanal',
    weeklyOpus: 'Opus semanal',
    extraUsage: 'Uso extra',
    // Sprint usage-limits-v2 (2026-07-08) — etiquetas para el array
    // canónico `limits[]` que Anthropic ahora expone.
    limitSession: 'Sesión (5h)',
    limitWeeklyAll: 'Semanal (7d)',
    limitWeeklyScoped: 'Semanal · scoped',
    limitWeeklyScopedModel: 'Semanal · {model}',
    // Sub-etiqueta en rojo cuando el límite está activo o al 100 %.
    limitReached: 'Límite alcanzado — resetea en {time}',
    // Copy de fallo per-runner (founder-mode). Cada clave mapea a un
    // `usageErrorCode` que el bridge devuelve. Lenguaje declarativo +
    // accionable: siempre describe qué pasó y qué hacer.
    errorOauthMissing: 'No hay credenciales de Claude en {hostname}. Ejecuta `claude login` en el runner.',
    errorOauthInvalid: 'El token de Claude expiró en {hostname}. Ejecuta `claude login` en el runner.',
    errorRateLimited: 'Anthropic rate-limited el endpoint de uso para {hostname}. Reinténtalo en unos minutos.',
    // Sprint usage-backoff-truth (D013-EC-148) — se usa en lugar de la
    // línea de arriba cuando sabemos a qué hora es el próximo intento.
    errorRateLimitedRetryAt: 'Anthropic limitó {hostname}. Reintento automático a las {time}.',
    errorUpstream: 'No se pudo obtener el uso de {hostname}. Reinténtalo en un momento.',
    errorTimeout: '{hostname} no respondió a tiempo. El runner probablemente está saturado — reinténtalo en breve.',
    errorRunnerOffline: 'Runner offline desde {lastSeen}. Reconéctalo para ver el uso.',
  },

  // ── Bloque de tool call ───────────────────────────────────────────────
  toolCall: {
    // Sprint orphan-tool-rows-honest (D013-EC-147) — quiet terminal label
    // for a tool whose result never arrived (process died mid-tool).
    interrupted: 'Interrumpido',
    running: '(ejecutando…)',
    error: '(error)',
    args: 'Args',
    errorLabel: 'Error',
    result: 'Resultado',
    empty: '(vacío)',
    showLess: 'Mostrar menos',
    showMore: 'Mostrar más ({chars} caracteres)',
    inputs: 'Entradas',
    // Sprint monitor-tool-live-timer — contador en vuelo en las tool rows
    // que siguen abiertas (Monitor / cualquier tool que supere los 2 s).
    waitingSeconds: 'Esperando {s}s',
    waitingMinutes: 'Esperando {m}m {s}s',
    waitingHours: 'Esperando {h}h {m}m',
  },

  // ── Visor de documentos (D-012, sprint md-doc-viewer; D-019 descarga) ─
  doc: {
    loading: 'Cargando…',
    notFound: '{path} ya no existe en este proyecto.',
    tooLarge: 'Este documento supera los 25 MB.',
    forbidden: 'Esta ruta no está permitida.',
    sessionExpired: 'Sesión caducada. Recarga la página.',
    networkError: 'No se pudo cargar el documento. Revisa la conexión.',
    retry: 'Reintentar',
    close: 'Cerrar',
    download: 'Descargar',
    noWorkdir: 'Este agente todavía no tiene directorio de trabajo.',
    pdfIosFallback:
      'Vista previa no disponible en iOS. Toca Descargar para abrir este PDF.',
  },

  // ── Superficie de rendering AI Elements (sprint ai-elements-rendering) ─
  aiElements: {
    chainOfThought: {
      header: 'Pasos de razonamiento',
    },
    reasoning: {
      thinking: 'Pensando…',
      thoughtBriefly: 'Pensó durante unos segundos',
      thoughtForSeconds: 'Pensó durante {duration} segundos',
    },
    codeBlock: {
      copy: 'Copiar',
      copied: 'Copiado',
      copyAria: 'Copiar código al portapapeles',
      copiedAria: 'Código copiado al portapapeles',
    },
    attachments: {
      image: 'Imagen',
      file: 'Adjunto',
      remove: 'Quitar adjunto',
      uploadFiles: 'Subir archivos',
      addPhotosOrFiles: 'Añadir fotos o archivos',
      errorAccept: 'Ningún archivo coincide con los tipos permitidos.',
      errorMaxFileSize: 'Todos los archivos superan el tamaño máximo.',
      errorMaxFiles: 'Demasiados archivos. Algunos no se añadieron.',
    },
  },

  ds4Mockup: {
    notificationsTooltip: '3 agentes necesitan tu atención',
    conversationOutOfScope: '(conversación — fuera de alcance de esta maqueta)',
    composer: {
      history1: 'He terminado la revisión del PR #482. Todo verde salvo un warning de lint en el módulo de settings.',
      history2: 'Vale, arréglalo y sigue con el siguiente punto del plan.',
      history3: 'Hecho. Ahora estoy ejecutando la suite de tests del backend completa antes de continuar.',
    },
    settings: {
      title: 'Ajustes',
      description: 'Preferencias del workspace',
      cancel: 'Cancelar',
      save: 'Guardar',
      telegramNotifications: 'Notificaciones de Telegram',
      autoUpdate: 'Actualizar automáticamente',
      devDetails: 'Mostrar detalles de desarrollador',
      dangerZone: 'Zona de peligro',
      dangerZoneHint: 'Eliminar este workspace es irreversible.',
      deleteWorkspace: 'Eliminar workspace',
    },
  },
} as const;

export default es;
